import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-feather';
import { taskApi } from '../../services/api';
import { canHaveSubtask, TASK_TYPE_SUBTASK } from '../../constants/taskTypes';
import { Button } from '../ui/button';
import { FeatherX, FeatherPlus } from '../ui/icons';
import { Tooltip } from '../ui/Tooltip';
import RelationshipBadge from './RelationshipBadge';
import './TaskLinks.css';

const RELATIONSHIP_TYPES = [
    { value: 'depends_on', label: 'Depends on' },
    { value: 'blocked_by', label: 'Blocked by' },
    { value: 'duplicates', label: 'Duplicate' },
    { value: 'subtask', label: 'Subtask' }
];

const TaskLinks = ({ task, allTasks = [], projectId, onTaskClick, isManager = false, allowCrossProject = false, onAddSubtask, onTaskUpdate, onLinksUpdated }) => {
    const [linkedTasks, setLinkedTasks] = useState([]);
    const [loading, setLoading] = useState(false);
    const [showAddLink, setShowAddLink] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedTaskId, setSelectedTaskId] = useState('');
    const [selectedRelationshipType, setSelectedRelationshipType] = useState('depends_on');
    const [adding, setAdding] = useState(false);
    const [error, setError] = useState('');

    useEffect(() => {
        if (task?.id) {
            // Use pre-fetched linked_tasks if available (from project API)
            if (task.linked_tasks && Array.isArray(task.linked_tasks)) {
                setLinkedTasks(task.linked_tasks);
                setLoading(false);
            } else {
                // Fallback to API call if linked_tasks not pre-loaded
                fetchLinkedTasks();
            }
        }
    }, [task?.id, task?.linked_tasks]);

    const fetchLinkedTasks = async () => {
        try {
            setLoading(true);
            setError('');
            const links = await taskApi.getLinks(task.id);
            setLinkedTasks(links || []);
            return links || [];
        } catch (err) {
            console.error('Error fetching linked tasks:', err);
            setError('Failed to load linked tasks');
            return [];
        } finally {
            setLoading(false);
        }
    };

    const handleAddLink = async () => {
        if (!selectedTaskId) {
            setError('Please select a task to link');
            return;
        }

        // When linking as subtask, confirm conversion if the linked task will be changed to type Subtask
        if (selectedRelationshipType === 'subtask') {
            const linkedTask = allTasks.find(t => t.id === selectedTaskId);
            if (linkedTask && linkedTask.task_type !== TASK_TYPE_SUBTASK) {
                const taskLabel = linkedTask.display_id ? `${linkedTask.display_id}: ${linkedTask.title}` : linkedTask.title;
                const confirmed = window.confirm(
                    `This will convert "${taskLabel}" to type "Subtask" and add it as a linked subtask. Continue?`
                );
                if (!confirmed) return;
            }
        }

        try {
            setAdding(true);
            setError('');
            await taskApi.createLink(task.id, selectedTaskId, selectedRelationshipType);

            // When linking as subtask, convert the linked task to type Subtask
            if (selectedRelationshipType === 'subtask') {
                const linkedTask = allTasks.find(t => t.id === selectedTaskId);
                if (linkedTask && linkedTask.task_type !== TASK_TYPE_SUBTASK) {
                    const updatedTask = await taskApi.update(selectedTaskId, { task_type: TASK_TYPE_SUBTASK });
                    onTaskUpdate?.(updatedTask);
                }
            }

            const links = await fetchLinkedTasks();
            onLinksUpdated?.(task.id, links);
            setShowAddLink(false);
            setSearchTerm('');
            setSelectedTaskId('');
            setSelectedRelationshipType('depends_on');
        } catch (err) {
            console.error('Error creating link:', err);
            setError(err.response?.data?.error || 'Failed to create link');
        } finally {
            setAdding(false);
        }
    };

    const handleDeleteLink = async (linkId) => {
        if (!window.confirm('Are you sure you want to remove this link?')) {
            return;
        }

        try {
            await taskApi.deleteLink(task.id, linkId);
            const links = await fetchLinkedTasks();
            onLinksUpdated?.(task.id, links);
        } catch (err) {
            console.error('Error deleting link:', err);
            setError(err.response?.data?.error || 'Failed to delete link');
        }
    };

    // Filter tasks for selection
    const availableTasks = allTasks.filter(t => {
        // Don't show the current task
        if (t.id === task.id) return false;
        
        // Don't show already linked tasks
        if (linkedTasks.some(link => link.linked_task.id === t.id)) return false;
        
        // Filter by search term
        if (searchTerm) {
            const searchLower = searchTerm.toLowerCase();
            const matchesId = t.display_id?.toLowerCase().includes(searchLower);
            const matchesTitle = t.title?.toLowerCase().includes(searchLower);
            if (!matchesId && !matchesTitle) return false;
        }
        
        // Filter by project if cross-project not allowed
        if (!allowCrossProject && projectId && t.project_id !== projectId) {
            return false;
        }
        
        return true;
    });

    // Group linked tasks by relationship type (direction)
    const groupedLinks = useMemo(() => {
        const groups = {};
        linkedTasks.forEach(link => {
            const direction = link.direction || link.relationship_type;
            if (!groups[direction]) {
                groups[direction] = [];
            }
            groups[direction].push(link);
        });
        return groups;
    }, [linkedTasks]);

    if (!task) return null;

    // Determine if we should show the section
    // Show if: has links, is adding, has error, is loading, OR is manager (so they can add links)
    const shouldShow = linkedTasks.length > 0 || showAddLink || error || loading || isManager;
    
    if (!shouldShow) return null;

    return (
        <div className="task-links">
            <div className="task-links-header">
                <div className="flex items-center gap-1.5">
                    <Link size={14} style={{ color: '#9ca3af', opacity: 0.7 }} />
                    <span className="text-body font-body text-default-font" style={{ fontWeight: 400, fontSize: '0.8125rem', color: '#6b7280' }}>
                        Linked Tasks
                    </span>
                    {linkedTasks.length > 0 && (
                        <span className="text-caption font-caption text-subtext-color" style={{ fontSize: '0.75rem', color: '#9ca3af' }}>
                            ({linkedTasks.length})
                        </span>
                    )}
                    {isManager && (
                        <button
                            className="task-links-add-icon"
                            onClick={() => setShowAddLink(!showAddLink)}
                            title={showAddLink ? 'Cancel' : 'Add link'}
                        >
                            <FeatherPlus size={10} />
                        </button>
                    )}
                </div>
                {isManager && onAddSubtask && canHaveSubtask(task.task_type) && (
                    <button
                        type="button"
                        className="task-links-add-subtask-btn"
                        onClick={() => onAddSubtask(task)}
                    >
                        Add subtask
                    </button>
                )}
            </div>

            {error && (
                <div className="task-links-error">
                    {error}
                </div>
            )}

            {showAddLink && isManager && (
                <div className="task-links-add-form">
                    <div className="task-links-search">
                        <input
                            type="text"
                            placeholder="Search tasks by ID or title..."
                            value={searchTerm}
                            onChange={(e) => {
                                setSearchTerm(e.target.value);
                                if (selectedTaskId) setSelectedTaskId('');
                            }}
                            className="task-links-search-input"
                        />
                        {searchTerm && availableTasks.length > 0 && !selectedTaskId && (
                            <div className="task-links-dropdown">
                                {availableTasks.slice(0, 10).map(t => (
                                    <div
                                        key={t.id}
                                        className="task-links-dropdown-item"
                                        onClick={() => {
                                            setSelectedTaskId(t.id);
                                            setSearchTerm(t.display_id || t.title);
                                        }}
                                    >
                                        <div className="task-links-dropdown-item-id">
                                            {t.display_id || `TASK-${t.id.substring(0, 8)}`}
                                        </div>
                                        <div className="task-links-dropdown-item-title">{t.title}</div>
                                        {t.project && t.project.id !== projectId && (
                                            <div className="task-links-dropdown-item-project">
                                                {t.project.name}
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                    <div className="task-links-relationship-select">
                        <label>Relationship Type:</label>
                        <select
                            value={selectedRelationshipType}
                            onChange={(e) => setSelectedRelationshipType(e.target.value)}
                            className="task-links-select"
                        >
                            {RELATIONSHIP_TYPES.map(type => (
                                <option key={type.value} value={type.value}>
                                    {type.label}
                                </option>
                            ))}
                        </select>
                    </div>
                    <div className="task-links-add-actions">
                        <Button
                            variant="neutral-tertiary"
                            onClick={() => {
                                setShowAddLink(false);
                                setSearchTerm('');
                                setSelectedTaskId('');
                                setError('');
                            }}
                        >
                            Cancel
                        </Button>
                        <Button
                            variant="brand-primary"
                            onClick={handleAddLink}
                            disabled={!selectedTaskId || adding}
                        >
                            {adding ? 'Adding...' : 'Add Link'}
                        </Button>
                    </div>
                </div>
            )}

            {loading ? (
                <div className="task-links-loading">Loading linked tasks...</div>
            ) : linkedTasks.length > 0 ? (
                <div className="task-links-list">
                    {Object.entries(groupedLinks).map(([relationshipType, links]) => (
                        <div key={relationshipType} className="task-links-group">
                            <RelationshipBadge
                                relationshipType={links[0].relationship_type}
                                direction={relationshipType}
                            />
                            <div className="task-links-group-items">
                                {links.map(link => {
                                    const taskId = link.linked_task.display_id || `TASK-${link.linked_task.id.substring(0, 8)}`;
                                    const tooltipContent = (
                                        <div>
                                            <div style={{ fontWeight: 600, marginBottom: '0.25rem' }}>
                                                {link.linked_task.title}
                                            </div>
                                            <div style={{ fontSize: '0.75rem', color: '#d1d5db' }}>
                                                Status: {link.linked_task.status}
                                            </div>
                                            {link.linked_task.project && link.linked_task.project.id !== projectId && (
                                                <div style={{ fontSize: '0.75rem', color: '#d1d5db', marginTop: '0.25rem' }}>
                                                    Project: {link.linked_task.project.name}
                                                </div>
                                            )}
                                        </div>
                                    );
                                    
                                    return (
                                        <div key={link.id} className="task-links-item-compact">
                                            <Tooltip content={tooltipContent} placement="top">
                                                <button
                                                    className="task-links-item-task-compact"
                                                    onClick={() => onTaskClick && onTaskClick(link.linked_task.id)}
                                                >
                                                    {taskId}
                                                </button>
                                            </Tooltip>
                                            {isManager && (
                                                <button
                                                    className="task-links-item-delete-compact"
                                                    onClick={() => handleDeleteLink(link.id)}
                                                    title="Remove link"
                                                >
                                                    <FeatherX size={10} />
                                                </button>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    ))}
                </div>
            ) : !showAddLink ? (
                <div className="task-links-empty">
                    {isManager ? 'No linked tasks. Click "Add Link" to create one.' : 'No linked tasks'}
                </div>
            ) : null}
        </div>
    );
};

export default TaskLinks;

